/**
 * Trial plan limits.
 * Used for UI fallbacks and messages; API returns live values where applicable.
 * Keep in sync with backend/constants/trialLimits.js.
 */
/** Token and model config per section. 1 credit = token tokens. */
export const SECTION_CONFIG = {
  script_creator: { token: 100000, model: '5.1' },
  test_launch: { token: 1000000, model: '4o-mini' },
  script_manager: { token: 1000000, model: '5.1' },
  /** Automation Studio replay — same token basis as script_manager; model aligned with replay_flow */
  automation_replay: { token: 1000000, model: '5-mini' },
};

/** Tokens per credit for Script Creator */
export const TOKENS_PER_CREDIT = SECTION_CONFIG.script_creator.token;

/** Tokens per credit for Test Launch */
export const TOKENS_PER_LAUNCH = SECTION_CONFIG.test_launch.token;

/** Tokens per credit for Script Manager */
export const TOKENS_PER_MANAGER = SECTION_CONFIG.script_manager.token;

export const MIN_CREDITS_TO_START = 3;
/** Minimum credits required to start Script Manager */
export const MIN_CREDITS_TO_START_SCRIPT_MANAGER = 0.5;
/** Minimum credits required to start Test Launch */
export const MIN_CREDITS_FOR_LAUNCH = 0.5;
/** Script Creator cap buffer as percentage of credit_limit */
export const SCRIPT_CREATOR_CAP_PERCENT = 10;

// Trial plan default limits
export const DEFAULT_TRIAL_CREDIT_LIMIT = 50;

/** Trial AI model keys per feature_key — keep in sync with backend/constants/trialLimits.js TRIAL_FEATURE_MODEL_KEYS */
export const TRIAL_FEATURE_MODEL_KEYS = Object.freeze({
  automation_recording: 'gpt-5.1',
  replay_flow: 'gpt-5-mini',
  test_launch: 'gpt-5-mini',
  ai_generator: 'gpt-5-mini',
  security_guardrails: 'gpt-5-mini',
  slack_assistant: 'gpt-5-mini',
  // Provider-only sentinel (not a catalog model). Trial uses OpenAI trial key.
  automation_new: 'agent_config',
});

/** Cap on credits_used during a run: credit_limit × (1 + SCRIPT_CREATOR_CAP_PERCENT/100). Matches backend getScriptCreatorCap. */
export function getScriptCreatorCap(creditLimit) {
  const limit = Number(creditLimit) || 0;
  return limit * (1 + SCRIPT_CREATOR_CAP_PERCENT / 100);
}

/**
 * Get tokens-per-credit for a section.
 * @param {string} section - 'script_creator' | 'test_launch' | 'script_manager' | 'automation_replay'
 * @returns {number}
 */
export function getTokensForSection(section) {
  const config = SECTION_CONFIG[section];
  return config ? Number(config.token) || TOKENS_PER_CREDIT : TOKENS_PER_CREDIT;
}

/**
 * Get model for a section.
 * @param {string} section - 'script_creator' | 'test_launch' | 'script_manager' | 'automation_replay'
 * @returns {string}
 */
export function getModelForSection(section) {
  const config = SECTION_CONFIG[section];
  return config ? String(config.model || '5.1') : '5.1';
}
